/* =============================================================
   MSAL.js configuration — single PublicClientApplication instance
   used everywhere we need an Entra access token. The instance is
   initialised once at module load; main.tsx awaits initialize()
   before rendering the React tree (MSAL requirement in v3).

   Env vars (all VITE_-prefixed so Vite bundles them at build time):
     VITE_ENTRA_AUTHORITY — e.g. https://prajnainnovates.ciamlogin.com/<tenantId>
     VITE_ENTRA_CLIENT_ID — the SPA app registration's client id
     VITE_API_SCOPE       — api://<api-app-id>/access_as_user
     VITE_API_BASE_URL    — Function App base URL. Empty in prod
                            (SWA proxies /api/* to the linked
                            Functions); the deployed URL in dev so
                            we can run vite dev against prod APIs.

   Fallbacks point at the real prod values so a missing .env.local
   doesn't break a fresh checkout — secrets are not involved
   (these are all public app-registration identifiers).
   ============================================================ */

import { PublicClientApplication, EventType, type Configuration } from '@azure/msal-browser';

const tenantId = '00640434-703b-438a-bca5-d6abd3d5278d';

export const ENTRA_AUTHORITY =
  import.meta.env.VITE_ENTRA_AUTHORITY ??
  `https://prajnainnovates.ciamlogin.com/${tenantId}`;

export const ENTRA_CLIENT_ID =
  import.meta.env.VITE_ENTRA_CLIENT_ID ?? '91a60f03-bfde-412a-b354-ec279eb6cf6b';

export const API_SCOPE =
  import.meta.env.VITE_API_SCOPE ??
  'api://d0e58355-b39f-4865-8c2a-64c367cd5ba4/access_as_user';

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  'https://func-prajnainnovates-g9hkfnbhddhvf3g8.westus2-01.azurewebsites.net';

const msalConfig: Configuration = {
  auth: {
    clientId: ENTRA_CLIENT_ID,
    authority: ENTRA_AUTHORITY,
    knownAuthorities: ['prajnainnovates.ciamlogin.com'],
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: `${window.location.origin}/`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/* MSAL v3 requires initialize() to be awaited before any auth call.
   We export the promise so main.tsx can gate render on it; React
   pages just call msalInstance methods normally after that. */
export const msalReady = msalInstance.initialize().then(() => {
  /* If we landed back from a redirect, MSAL needs handleRedirectPromise()
     called once. Pages that own the /auth/callback route also call it,
     but having it here covers the case where the user reloads while still
     mid-redirect. */
  return msalInstance.handleRedirectPromise().catch((e) => {
    console.warn('[msal] handleRedirectPromise on init failed:', e);
  });
});

/* Sign out of the app and Entra in one go.
   - logoutHint (the token's login_hint claim) lets Entra skip its "which
     account do you want to sign out of?" picker. The claim is only present
     once it's enabled under the SPA app registration's Token configuration.
   - Entra returns to the registered post-logout URL (the app root, which is
     blank for now); the flag set here makes the root forward to /account. */
export async function signOutEverywhere(): Promise<void> {
  await msalReady;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? undefined;
  const hint = (account?.idTokenClaims as { login_hint?: string } | undefined)?.login_hint;
  try {
    sessionStorage.setItem('signed-out', '1');
    sessionStorage.removeItem('rq-cache:v1'); // main.tsx clears it again on the next load, in case a write lands after this
  } catch {
    /* ignore */
  }
  await msalInstance.logoutRedirect({ account, logoutHint: hint, postLogoutRedirectUri: `${window.location.origin}/` });
}

/* Keep the active account in sync with MSAL's notion of "the last
   account that signed in". React components subscribe via a custom
   event in lib/auth.tsx. */
msalInstance.addEventCallback((evt) => {
  if (
    (evt.eventType === EventType.LOGIN_SUCCESS ||
      evt.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      evt.eventType === EventType.SSO_SILENT_SUCCESS) &&
    evt.payload &&
    'account' in evt.payload &&
    evt.payload.account
  ) {
    msalInstance.setActiveAccount(evt.payload.account);
  }
});
